import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';

import type { InternalUserProfileRequest } from '@ndvr/auth-api';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import type { InternalUserProfileRecord } from './internal-user-profile-queries';

type InternalUserProfileFormValues = {
    email: string;
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    bio: string;
    calendarLink: string;
    imageSrc: string;
};

const emptyProfileValues: InternalUserProfileFormValues = {
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    phone: '',
    bio: '',
    calendarLink: '',
    imageSrc: '',
};

const trimToOptional = (value?: string | null): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
};

const normalizePhoneDigits = (value?: string | null): string => {
    const digits = value?.replace(/\D/g, '') ?? '';
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const formatPhoneForRequest = (value?: string | null): string | undefined => {
    const digits = normalizePhoneDigits(value);
    if (!digits) {
        return undefined;
    }

    if (digits.length !== 10) {
        return digits;
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const buildInternalUserProfileRequest = (values: InternalUserProfileFormValues): InternalUserProfileRequest => {
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();

    return {
        email: values.email.trim(),
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        title: trimToOptional(values.title),
        phone: formatPhoneForRequest(values.phone),
        bio: trimToOptional(values.bio),
        calendarLink: trimToOptional(values.calendarLink),
        imageSrc: trimToOptional(values.imageSrc),
    };
};

export const InternalUserProfileForm = ({
    profile,
    isPending,
    onSubmit,
    submitLabel = 'Save Changes',
    onCancel,
}: {
    profile: InternalUserProfileRecord;
    isPending: boolean;
    onSubmit: (profile: InternalUserProfileRequest) => void;
    submitLabel?: string;
    onCancel?: () => void;
}) => {
    const form = useForm<InternalUserProfileFormValues>({
        mode: 'onChange',
        defaultValues: emptyProfileValues,
    });

    useEffect(() => {
        form.reset({
            email: profile.email ?? '',
            firstName: profile.firstName ?? '',
            lastName: profile.lastName ?? '',
            title: profile.title ?? '',
            phone: normalizePhoneDigits(profile.phone),
            bio: profile.bio ?? '',
            calendarLink: profile.calendarLink ?? '',
            imageSrc: profile.imageSrc ?? '',
        });
    }, [form, profile]);

    const handleSubmit = form.handleSubmit((values) => {
        onSubmit(buildInternalUserProfileRequest(values));
    });

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4">
                <FormField
                    control={form.control}
                    name="email"
                    rules={{ required: 'Email is required' }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                                <Input {...field} type="email" placeholder="name@ndvr.com" disabled={isPending} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="First name" disabled={isPending} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="Last name" disabled={isPending} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="Wealth Advisor" disabled={isPending} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                                <PatternFormat
                                    id={field.name}
                                    valueIsNumericString
                                    value={field.value}
                                    onValueChange={(value) => field.onChange(value.value)}
                                    onBlur={field.onBlur}
                                    getInputRef={field.ref}
                                    customInput={Input}
                                    format="###-###-####"
                                    placeholder="617-555-0100"
                                    disabled={isPending}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Bio</FormLabel>
                            <FormControl>
                                <Textarea
                                    {...field}
                                    placeholder="Short advisor biography"
                                    disabled={isPending}
                                    rows={4}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="calendarLink"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Calendar Link</FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    type="url"
                                    placeholder="https://calendly.com/..."
                                    disabled={isPending}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="imageSrc"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Image URL</FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    type="url"
                                    placeholder="https://cdn.example.com/profile.png"
                                    disabled={isPending}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="mt-auto flex justify-end gap-2">
                    {onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        type="submit"
                        disabled={isPending || !form.watch('email')?.trim() || !form.formState.isDirty}
                    >
                        {isPending ? 'Saving...' : submitLabel}
                    </Button>
                </div>
            </form>
        </Form>
    );
};
